<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import PropertyView from "@/shared/components/views/property-view.vue";
import { PropertyItem } from "@/shared/components/views/property-item.model.ts";

export interface ListCardCollapsibleProps {
    title: string;
    localeTag: string;
    hitsCount: string;
    propertyItems: PropertyItem[];
    index?: number;
    testId?: string;
}

const props = defineProps<ListCardCollapsibleProps>();

const { index, testId } = toRefs(props);
const isExpanded = ref<boolean>(false);

const toggleCollapse = () => {
    isExpanded.value = !isExpanded.value;
};

const testIdentifier = computed(() => {
    if (testId.value) return testId.value;

    if (index.value !== undefined) return `list-card-${index.value}`;

    return null;
});

const collapseToggleTestId = computed(() =>
    testIdentifier.value ? `${testIdentifier.value}-collapse-toggle` : "list-card-collapse-toggle"
);
</script>

<template>
    <q-card
        class="list-card-collapsible"
        :class="{
            expanded: isExpanded,
        }"
        :data-qs="testIdentifier"
    >
        <div class="collapsible-layout">
            <div class="image-section">
                <div class="image-container">
                    <q-icon
                        size="42px"
                        class="fa-light"
                        :class="{ 'fa-folder': !isExpanded, 'fa-folder-open': isExpanded }"
                    />
                    <div v-if="localeTag" class="locale">
                        {{ localeTag.toUpperCase() }}
                    </div>
                </div>
            </div>
            <div class="collapsible-main">
                <div class="header-section">
                    <q-card-section class="middle-section">
                        <div>
                            <span class="title-semi-bold q-mr-sm" :data-qs="`list-card-collapsible-title-${title}`">
                                {{ title }}
                            </span>
                            <span class="count-text lighter-text-color">{{ `(${hitsCount})` }}</span>
                        </div>
                        <slot name="subtitle" />
                        <div v-if="propertyItems" class="row properties col-gap-l">
                            <PropertyView :items="propertyItems" :max-property-width="180" />
                        </div>
                    </q-card-section>
                </div>
                <q-slide-transition>
                    <div v-show="isExpanded" class="collapsible-content">
                        <slot name="content" :is-expanded="isExpanded" />
                    </div>
                </q-slide-transition>
            </div>
            <div
                class="collapse-toggle"
                role="button"
                :aria-expanded="isExpanded"
                :data-qs="collapseToggleTestId"
                @click="toggleCollapse"
            >
                <q-icon
                    name="fa-light fa-angle-up"
                    size="20px"
                    class="collapse-icon"
                    :class="{ rotated: !isExpanded }"
                />
            </div>
        </div>
    </q-card>
</template>

<style lang="scss" scoped>
.list-card-collapsible {
    box-shadow: $card-shadow;
    align-content: center;
    width: 100%;
    max-width: 100%;
    user-select: none;

    .collapsible-layout {
        display: flex;
        flex-direction: row;
    }

    .image-section {
        background-color: $icon-background-color;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding: $spacing-l;
    }

    .image-container {
        display: flex;
        flex-direction: column;
        align-items: center;

        i {
            color: $icon-color-light;
        }
    }

    .locale {
        font-size: 12px;
        color: $grey-55;
        line-height: 1;
    }

    .collapsible-main {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
    }

    .header-section {
        display: flex;
        flex-direction: row;
        min-height: 88px;
    }

    .middle-section {
        flex: 1;
        white-space: normal;
        overflow: hidden;
    }

    .collapse-toggle {
        cursor: pointer;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        width: 36px;
        min-width: 36px;
        padding-top: $spacing-xl;
        background-color: $icon-background-color;
    }

    .collapse-icon {
        transition: transform 0.2s ease;

        &.rotated {
            transform: rotate(180deg);
        }
    }

    .properties {
        margin-top: $spacing-ml;
    }

    .count-text {
        font-size: 16px;
    }
}
</style>
